import React from 'react';
import PropTypes from 'prop-types';
import './MetaBadge.scss';

/**
 * Compact semantic metadata for dense product surfaces such as table and task rows.
 * Use Badge for achievements; use MetaBadge for inline context and state labels.
 */
const MetaBadge = React.forwardRef(({
    as: Component = 'span',
    tone = 'neutral',
    size = 'small',
    interactive = false,
    className = '',
    children,
    ...props
}, ref) => {
    const combinedClassName = [
        'ds-meta-badge',
        `ds-meta-badge--${tone}`,
        `ds-meta-badge--${size}`,
        interactive ? 'ds-meta-badge--interactive' : '',
        className,
    ].filter(Boolean).join(' ');
    const componentProps = {
        ...(Component === 'button' ? { type: 'button' } : {}),
        ...props,
    };

    return (
        <Component ref={ref} className={combinedClassName} {...componentProps}>
            {children}
        </Component>
    );
});

MetaBadge.displayName = 'MetaBadge';

MetaBadge.propTypes = {
    /** Render element. Use button for interactive metadata. */
    as: PropTypes.elementType,
    /** Semantic color treatment. */
    tone: PropTypes.oneOf(['neutral', 'info', 'warning', 'purple', 'success', 'danger']),
    /** Density preset. */
    size: PropTypes.oneOf(['small', 'medium']),
    /** Enables hover and focus affordances. */
    interactive: PropTypes.bool,
    /** Additional CSS classes. */
    className: PropTypes.string,
    /** Badge content. */
    children: PropTypes.node,
};

export default MetaBadge;
